import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import connect, { type ConnectedProps } from 'react-redux/es/connect/connect';
import { Form } from 'antd';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { FiltersJob } from '_organisms';
import { EMPTY_FILTERS_VALUES, RESET_FILTERS_VALUES } from '_organisms/FiltersJob/constants';
import { Button, BUTTON_TYPES, Checkbox, Table, Tooltip } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { TableMobile } from '_atoms/Table/Table';
import { MixpanelService } from 'helpers/Mixpanel';
import * as actions from 'actions';
import { changeJobFilter } from 'store/reducers/jobReducer';
import { getUser } from 'store/selectors/authorizeSelectors';
import { getCompanies, getLocations } from 'store/selectors/commonSelectors';
import { getJobListFilters } from 'store/selectors/jobSelectors';
import history from 'helpers/history';
import { useQuery } from 'helpers/useLocation';
import { ACTIONS, Can, SUBJECTS, UI } from 'permission';
import { ROUTES } from 'router/constants';
import type { RootState } from 'store';
import type { JobListLiteItem, Opening } from 'store/reducers/jobReducer';
import type { PaginatedResponse } from 'store/reducers/types';
import { ReactComponent as Vector } from 'dist/icons/Vector1.svg';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import emptyImg from 'dist/emptyStates/dashboardOpening.svg';
import { filtersMapper, defaultFilters, type JobFilterValues, type JobTableOptions } from './utils';
import { INITIAL_VALUES, COLUMNS, expandedRowRender } from './constants';
import './style.scss';

interface LoadedOpeningsEntry {
  loaded?: boolean;
  loading?: boolean;
  openings?: Opening[];
}

export const JobsEmpty = () => {
  const { t } = useTranslation();
  return (
    <div className="empty-template">
      <img src={emptyImg} alt="" />
      <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:jobs.empty_title')}</Typography.Title2>
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:jobs.empty_subtitle')}</Typography.Title3>
    </div>
  );
};

const JobsSubTitle = ({ activeOpenings, waitingForEntry }: { activeOpenings: number; waitingForEntry: number }) => {
  const { t } = useTranslation();
  return (
    <div className="jobs-list-subtitle">
      <Typography.Title3>
        {t('translation:jobs.label_active_openings')}
        <Tooltip label={t('translation:jobs.tooltip_active_openings')}>
          <Info />
        </Tooltip>
        <span>{activeOpenings}</span>
      </Typography.Title3>
      <Typography.Title3>
        {t('translation:jobs.label_waiting_for_entry')}
        <span>{waitingForEntry}</span>
      </Typography.Title3>
    </div>
  );
};

function mapStateToProps(store: RootState) {
  return {
    filter: getJobListFilters(store),
    companies: getCompanies(store),
    locations: getLocations(store),
    user: getUser(store),
  };
}

function mapDispatchToProps(dispatch: Parameters<typeof bindActionCreators>[1]) {
  return {
    getAllCompanies: bindActionCreators(actions.getAllCompanies, dispatch),
    getListLocations: bindActionCreators(actions.getListLocations, dispatch),
    changeJobFilter: bindActionCreators(changeJobFilter, dispatch),
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const Jobs = (props: PropsFromRedux) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const location = useLocation();
  const query = useQuery();

  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS_VALUES,
    ...defaultFilters(props.user),
    ...props.filter,
    ...query,
  });
  const [promotionApplied, setPromotionApplied] = useState(
    [props.filter.promotion_applied, query.promotion_applied].some(value => value === true || value === 'true')
  );
  const [tableOptions, setTableOptions] = useState<JobTableOptions>(INITIAL_VALUES);
  const [loadedOpenings, setLoadedOpenings] = useState<Record<string, LoadedOpeningsEntry>>({});
  const [jobsList, setJobsList] = useState<JobListLiteItem[]>([]);
  const [jobsInfo, setJobsInfo] = useState({ activeOpenings: 0, waitingForEntry: 0 });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    props.getAllCompanies();
    const mixpanel = new MixpanelService();
    mixpanel.track('View Page.Jobs List');
  }, []);

  useEffect(() => {
    if (Object.keys(query).length) {
      history.replace(location.pathname);
    }
  }, [query]);

  const handleChangeDate = (field: string, item: unknown) => {
    if (field === 'page') {
      setTableOptions(s => ({ ...s, page: item as number, offset: ((item as number) - 1) * s.limit.value }));
    } else if (field === 'limit') {
      setTableOptions(s => ({ ...s, limit: item as JobTableOptions['limit'], offset: 0, page: 1 }));
    } else {
      setTableOptions(s => ({ ...s, [field]: item }));
    }
  };

  const setSorting = (sort: JobTableOptions['sorting']) => handleChangeDate('sorting', sort);

  const getJobs = (values: JobFilterValues = form.getFieldsValue(), promotionAppliedValue = promotionApplied) => {
    const filtersValues = { ...values, promotion_applied: promotionAppliedValue };

    props.changeJobFilter(filtersValues);
    setLoading(true);
    actions
      .getListJobs({ ...filtersMapper(filtersValues, tableOptions) }, 'v2')
      .then(resp => {
        const paginated = resp as PaginatedResponse<JobListLiteItem>;
        setLoading(false);
        setTableOptions(s => ({ ...s, itemsCount: paginated.count }));
        setJobsList(paginated.results);
        setExpandedRowKeys([]);
        setLoadedOpenings({});
      })
      .catch(() => {
        setLoading(false);
        setTableOptions(s => ({ ...s, itemsCount: 0 }));
        setJobsList([]);
        setExpandedRowKeys([]);
        setLoadedOpenings({});
      });
    actions.getOpeningsCount({ ...filtersMapper(filtersValues, tableOptions) }).then(data => {
      setJobsInfo({
        activeOpenings: data?.active_openings_count ?? 0,
        waitingForEntry: data?.waiting_for_entry_openings_count ?? 0,
      });
    });
  };

  const onApplyFilters = (
    values: JobFilterValues = form.getFieldsValue(),
    promotionAppliedValue = promotionApplied
  ) => {
    if (tableOptions.offset === 0 && tableOptions.page === 1) {
      getJobs(values, promotionAppliedValue);
    } else {
      setTableOptions(s => ({ ...s, offset: 0, page: 1 }));
    }
  };

  const resetFilter = () => {
    form.setFields(RESET_FILTERS_VALUES);
    props.changeJobFilter({});
    setFilters(EMPTY_FILTERS_VALUES);
    setPromotionApplied(false);
    onApplyFilters(EMPTY_FILTERS_VALUES, false);
  };

  const handlePromotionAppliedChange = (e: { target: { checked: boolean } }) => {
    const { checked } = e.target;

    setPromotionApplied(checked);
    onApplyFilters(form.getFieldsValue(), checked);
  };

  const onExpand =
    ({ uuid }: JobListLiteItem) =>
    () => {
      if (!loadedOpenings[uuid]?.loaded) {
        setLoadedOpenings(o => ({ ...o, [uuid]: { loaded: true, loading: true, openings: [] } }));
        actions
          .getOpeningsOnTheJobList(uuid)
          .then(openings => {
            setLoadedOpenings(o => ({ ...o, [uuid]: { loaded: true, loading: false, openings: openings ?? [] } }));
          })
          .catch(() => {
            setLoadedOpenings(o => ({ ...o, [uuid]: { loaded: true, loading: false, openings: [] } }));
          });
      }
      if (!expandedRowKeys.includes(uuid)) setExpandedRowKeys(rows => [...rows, uuid]);
      else setExpandedRowKeys(rows => rows.filter(r => r !== uuid));
    };

  useEffect(getJobs, [tableOptions.sorting, tableOptions.limit, tableOptions.offset, tableOptions.page]);

  const extra = (
    <div className="table-jobs__extra">
      <Can I={ACTIONS.READ} a={UI.JOB_LIST_PROMOTION_APPLIED}>
        <Checkbox
          label={t('translation:jobs.label_promotion_applied')}
          isSelected={promotionApplied}
          onChange={handlePromotionAppliedChange}
        />
      </Can>
      <Can I={ACTIONS.CREATE} a={SUBJECTS.JOB}>
        <Button type={BUTTON_TYPES.PRIMARY} onClick={() => history.push(ROUTES.JOBS_CREATE)}>
          {t('translation:jobs.button_add')}
        </Button>
      </Can>
    </div>
  );

  return (
    <div className="wrapper-content jobs-list">
      <FiltersJob
        form={form}
        onFinish={onApplyFilters}
        onReset={resetFilter}
        initialValues={filters}
        loading={loading}
      />
      <Table<JobListLiteItem>
        rowKey="uuid"
        className="table-jobs"
        cardTitle={t('translation:jobs.title')}
        subtitle={<JobsSubTitle activeOpenings={jobsInfo.activeOpenings} waitingForEntry={jobsInfo.waitingForEntry} />}
        cardCount={tableOptions.itemsCount}
        emptyComponent={<JobsEmpty />}
        extra={extra}
        columns={COLUMNS(tableOptions.sorting, setSorting, t)}
        data={jobsList}
        tooltipInfo={t('translation:jobs.tooltip_info')}
        limit={tableOptions.limit}
        page={tableOptions.page}
        updateParams={handleChangeDate as (key: string, value: number) => void}
        itemsCount={tableOptions.itemsCount}
        loading={loading}
        expandable={{
          expandedRowKeys,
          expandedRowRender: ({ uuid }: JobListLiteItem) => expandedRowRender(loadedOpenings[uuid] || {}, uuid, t),
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon: (iconProps: { expanded: boolean; record: JobListLiteItem }) => {
            const { expanded, record } = iconProps;
            return (
              <>
                <Vector className={cn('expanded-icon', { expanded })} onClick={onExpand(record)} />
                <TableMobile<JobListLiteItem>
                  data={[record]}
                  columns={COLUMNS(tableOptions.sorting, setSorting, t)}
                  empty={<JobsEmpty />}
                />
                <Button type={BUTTON_TYPES.TEXT} onClick={onExpand(record)} className="expanded-icon-mobile">
                  {expanded ? t('translation:jobs.button_hide_openings') : t('translation:jobs.button_show_openings')}
                </Button>
              </>
            );
          },
        }}
        showCardFooter={!!jobsList.length}
      />
    </div>
  );
};

export default connector(Jobs);
